import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  GitlabClient,
  GitlabProvisioningStatus,
  GitlabProvisioningStatusResponse,
} from '../httpClients/GitlabClient';
import { QUERY_KEYS } from './consts';

const RETRY_LIMIT = 3;

export const PROVISION_STATUS_POLL_INTERVAL_MS = 2000;

const TERMINAL_STATUSES: ReadonlySet<GitlabProvisioningStatus> = new Set([
  'done',
  'failed',
]);

/**
 * Returns true once provisioning has reached a terminal state and should no
 * longer be polled.
 * @param status - Status surfaced by the provision-status endpoint.
 * @returns True when provisioning is done or failed.
 */
export const isTerminalProvisioningStatus = (
  status: GitlabProvisioningStatus | undefined,
): boolean => !!status && TERMINAL_STATUSES.has(status);

/**
 * The refetchInterval for the provision-status poll: keep polling while the
 * status is non-terminal (or not yet known), stop on done/failed.
 * @param status - Latest status from the poll (undefined before the first tick).
 * @returns The poll interval in ms, or false to stop.
 */
export const getProvisionStatusRefetchInterval = (
  status: GitlabProvisioningStatus | undefined,
): number | false =>
  isTerminalProvisioningStatus(status)
    ? false
    : PROVISION_STATUS_POLL_INTERVAL_MS;

/**
 * Polls the v2 provisioning status until terminal (done/failed). Disabled until
 * a `provisionId` exists; polling stops automatically on a terminal status.
 * @param provisionId - Id returned by the provision mutation; query disabled while undefined.
 * @param options - Optional TanStack Query overrides.
 * @returns TanStack Query result wrapping `GitlabProvisioningStatusResponse`.
 */
export const useGitlabProvisionStatusQuery = (
  provisionId: string | undefined,
  options: Partial<
    UseQueryOptions<
      GitlabProvisioningStatusResponse,
      Error,
      GitlabProvisioningStatusResponse
    >
  > = {},
) =>
  useQuery({
    queryKey: [QUERY_KEYS.GITLAB_PROVISION_STATUS, provisionId],
    queryFn: () => GitlabClient.getProvisioningStatus(provisionId as string),
    enabled: provisionId !== undefined,
    refetchInterval: (query) =>
      getProvisionStatusRefetchInterval(query.state.data?.status),
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 404) return false;

      return failureCount < RETRY_LIMIT;
    },
    ...options,
  });
